/**
 * VisualPerceptionPage — «Зрительное восприятие» (раздел «Когнитивные навыки»).
 *
 * На экране КАРТИНКА С НАЛОЖЕННЫМИ ДРУГ НА ДРУГА ФИГУРАМИ (по центру);
 * пациент называет увиденное ВСЛУХ — счёт считает сервер.
 *
 * Сценарий:
 *   1. Старт СРАЗУ С КАРТИНКИ: никаких вводных экранов и отсчётов — как только
 *      карточка загружена, она ПОКАЗЫВАЕТСЯ по центру, вопрос ЗВУЧИТ на этой
 *      же странице и виден ТЕКСТОМ над картинкой, микрофон включается сразу
 *      после вопроса.
 *      ЗВУК ВОПРОСА — СНАЧАЛА СВОЙ АУДИОФАЙЛ из visual_bank (audio_url):
 *      страница играет его и включает микрофон по окончании. Нет файла / не
 *      загрузился / браузер заглушил автоплей — фолбэк: синтез речи
 *      (speechSynthesis, ru-RU), а если заглушён и он — старт без звука,
 *      вопрос остаётся текстом + кнопка 🔊 проговорит его по касанию.
 *   2. Каждая карточка показывается
 *      2 занятия подряд — как в «Беглости речи»: занятие (день) N → карточка
 *      ceil(N/2) (дни 1–2 → карточка 1 … дни 39–40 → карточка 20). Тип задания
 *      принадлежит КАРТОЧКЕ (сервер берёт его из visual_bank): нечётные
 *      карточки — «какие предметы вы видите?», чётные — «какие буквы вы
 *      видите?». Ответы карточки зашиты в db/visual_bank.sql; НА ЭКРАНЕ
 *      список ответов НЕ показывается (пациент не видит подсказки) — только
 *      счётчик «Названо X из N» и анонимные точки прогресса.
 *   3. Пациент называет фигуры В ЛЮБОМ ПОРЯДКЕ. Как только названо ВСЁ,
 *      задание закрывается само (автофиниш по локальному совпадению — счёт
 *      всё равно пересчитывает сервер из transcript). Страховки: кнопка
 *      «Готово» и мягкий лимит (config.softLimitSec, по умолчанию 3 минуты).
 *   4. Web Speech API (src/services/speechRecognition.js, ru-RU, continuous);
 *      Chrome сам останавливается на паузах — перезапускаем в onend.
 *   5. transcript + время уходят на POST /api/trainers/visual
 *      {stepId: 'v01'..'v20', elapsedMs, thinkingMs} — сервер считает
 *      count/matched/missed/errors и пишет в ОБЩУЮ таблицу trainer_reports
 *      (step_id ПО КАРТОЧКЕ — оба занятия пары пишут в один шаг,
 *      algorithm_version='visual-web-1'). elapsedMs — общее время (микрофон
 *      включён → финиш), thinkingMs — «на раздумье» (микрофон включён →
 *      первое распознанное слово; не говорил ничего — равно общему).
 *
 * Фолбэк «Коснитесь экрана» — ТОЛЬКО если браузер не поддерживает распознавание
 * или микрофон не разрешён при автостарте.
 *
 * Маршрут: /visual — пациент сам; /visual?patientId=UUID — запуск врачом
 * из карточки пациента. Занятие (день) = 1..40: первое прохождение — день 1,
 * дальше календарные дни от первой visual-записи; ?day=N — проверить любой
 * день. Картинки — полный URL из visual_bank (Supabase Storage:
 * …/cortivox/visual/card01.png … card20.png, ПО КАРТОЧКЕ, не по дню);
 * страница сама перебирает .png/.jpg/.jpeg того же имени, если файл
 * назван иначе. Ответы — в db/visual_bank.sql.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BrainCircuit,
  CheckCircle2,
  Eye,
  Flag,
  Loader2,
  Mic,
  RotateCcw,
  Volume2,
} from 'lucide-react';
// Клиент — общий cortivoxApi.js (тот же request-хелпер, что и у «Беглости»):
// getVisualSession / saveVisualReport добавлены туда же, отдельный
// visualApi.js не нужен.
import {
  getAuthUserId,
  getVisualSession,
  listTrainerReports,
  localToday,
  saveVisualReport,
} from '../lib/cortivoxApi';
import { createRecognition, isRecognitionSupported } from '../services/speechRecognition';

const PROGRAM_DAYS = 40;
const DEFAULT_SOFT_LIMIT_MS = 180_000; // мягкий лимит, если сервер не задал
const MAX_WORDS = 150;                 // защита от аномально длинного transcript
const VOICE_GRACE_MS = 1200;           // ждём автоголос (TTS): заглушён — стартуем без него
const VOICE_CAP_MS = 8000;             // страховка от «зависшего» onend у TTS
const AUDIO_CAP_MS = 30_000;           // страховка от «зависшего» аудиофайла инструкции

const VISUAL_STEP_RE = /^v\d+$/; // шаги визуального тренажёра в trainer_reports

// Занятие (день программы) 1..40 → карточка 1..20: каждую показывают
// 2 занятия подряд, stepId отчёта — ПО КАРТОЧКЕ, как в «Беглости речи».
const cardForDay = (d) => Math.ceil((Number(d) || 1) / 2);
const stepIdForDay = (d) => `v${String(cardForDay(d)).padStart(2, '0')}`;

// --- лёгкий клиентский матчинг (только для автофиниша/прогресса!) ----------
// Авторитетный счёт делает СЕРВЕР (api/app/scoring/visual.py). Здесь —
// упрощённая копия: точное совпадение + срез частых окончаний + словарь
// названий букв. Расхождение с сервером не страшно: в худшем случае автофиниш
// сработает чуть позже (пациент нажмёт «Готово» или сработает мягкий лимит).

const PUNCT_RE = /[^0-9a-zа-яё\s-]/gi;

const normalizeWord = (w, foldYo = true) => {
  let s = String(w).toLowerCase().trim();
  if (foldYo) s = s.replace(/ё/g, 'е');
  return s.replace(PUNCT_RE, ' ').replace(/\s+/g, ' ').trim();
};

const ENDINGS = [
  'ами', 'ями', 'ого', 'его', 'ому', 'ему', 'ыми', 'ими', 'ах', 'ях', 'ам',
  'ям', 'ов', 'ев', 'ой', 'ей', 'ый', 'ий', 'ая', 'яя', 'ом', 'ем',
  'ы', 'и', 'а', 'я', 'у', 'ю', 'е', 'ь',
];

const stemWord = (w) => {
  if (w.length <= 4) return w;
  for (const end of ENDINGS) {
    // для род. падежа мн. числа (-ов/-ев/-ей) допускаем основу от 3 букв:
    // «очков» → «очк», совпадёт с «очки» через startsWith (pymorphy на
    // клиенте нет — это приблизительный матчинг для автофиниша)
    const minStem = end === 'ов' || end === 'ев' || end === 'ей' ? 3 : 4;
    if (w.endsWith(end) && w.length - end.length >= minStem) return w.slice(0, w.length - end.length);
  }
  return w;
};

// Названия букв вслух → литера (как на сервере; ключи без ё→е).
const LETTER_NAMES = {
  а: 'А', бэ: 'Б', бе: 'Б', бей: 'Б', вэ: 'В', ве: 'В', гэ: 'Г', ге: 'Г',
  дэ: 'Д', де: 'Д', е: 'Е', ё: 'Ё', жэ: 'Ж', же: 'Ж', зэ: 'З', зе: 'З',
  и: 'И', ий: 'Й', 'и краткое': 'Й', 'и-краткое': 'Й', ка: 'К', эль: 'Л',
  эл: 'Л', эм: 'М', ем: 'М', эн: 'Н', ен: 'Н', о: 'О', пэ: 'П', пе: 'П',
  эр: 'Р', эс: 'С', ес: 'С', тэ: 'Т', те: 'Т', у: 'У', эф: 'Ф', еф: 'Ф',
  ха: 'Х', це: 'Ц', че: 'Ч', ша: 'Ш', ше: 'Ш', ща: 'Щ', ще: 'Щ',
  'твёрдый знак': 'Ъ', 'твердый знак': 'Ъ', 'твёрдый-знак': 'Ъ', 'твердый-знак': 'Ъ',
  кэ: 'К', лэ: 'Л', мэ: 'М', нэ: 'Н', рэ: 'Р', сэ: 'С',
  фэ: 'Ф', хэ: 'Х', цэ: 'Ц', чэ: 'Ч', шэ: 'Ш', щэ: 'Щ',
  ы: 'Ы', 'мягкий знак': 'Ь', 'мягкий-знак': 'Ь', э: 'Э', ю: 'Ю', я: 'Я',
};

const spokenToLetter = (token) => {
  const low = normalizeWord(token, false);
  if (LETTER_NAMES[low]) return LETTER_NAMES[low];
  if (low.length === 1 && 'абвгдеёжзийклмнопрстуфхцчшщъыьэюя'.includes(low)) return low.toUpperCase();
  return null;
};

const FILLERS = new Set(['и', 'а', 'но', 'вот', 'э', 'ну', 'эм', 'у', 'это', 'же', 'как', 'бы', 'что', 'да']);

// Совпадение потока токенов со списком ответов → {matched:Set<index>, errors:[...]}
const matchTranscript = (transcript, answers, task) => {
  const foldYo = task !== 'letters';
  const toks = normalizeWord(transcript || '', foldYo).split(' ').filter(Boolean).slice(0, MAX_WORDS);
  const variants = answers.map((a) =>
    [a.answer, ...(a.aliases || [])].map((v) => normalizeWord(v, foldYo)).filter(Boolean),
  );
  const letterWants = task === 'letters'
    ? answers.map((a) => spokenToLetter(a.answer))
    : [];

  const taken = new Set();
  const consumed = new Array(toks.length).fill(false);
  const errors = [];
  const used = new Set();

  const hit = (phrase) => {
    if (task === 'letters') {
      const letter = spokenToLetter(phrase);
      if (!letter) return -1;
      for (let k = 0; k < letterWants.length; k += 1) {
        if (!taken.has(k) && letterWants[k] === letter) return k;
      }
      return -1;
    }
    const norm = normalizeWord(phrase, foldYo);
    const stem = stemWord(norm);
    for (let k = 0; k < variants.length; k += 1) {
      if (taken.has(k)) continue;
      for (const v of variants[k]) {
        if (v === norm || (norm.length > 4 && (v === stem || v.startsWith(stem)))) return k;
      }
    }
    return -1;
  };

  // двухсловные названия — жадно, слева направо
  let i = 0;
  while (i < toks.length - 1) {
    const k = hit(`${toks[i]} ${toks[i + 1]}`);
    if (k >= 0) {
      taken.add(k);
      consumed[i] = consumed[i + 1] = true;
      i += 2;
      continue;
    }
    i += 1;
  }
  toks.forEach((t, idx) => {
    if (consumed[idx]) return;
    const k = hit(t);
    if (k >= 0) {
      taken.add(k);
      return;
    }
    if (!FILLERS.has(t) && !used.has(t)) {
      errors.push(t);
      used.add(t);
    }
  });
  return { matched: taken, errors };
};

const dayToUtc = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return Date.UTC(y || 1970, (m || 1) - 1, d || 1);
};

// День программы по истории visual-записей: первое прохождение = день 1.
const resolveDayFromReports = (items, todayIso) => {
  const dates = (Array.isArray(items) ? items : [])
    .filter((it) => VISUAL_STEP_RE.test(String(it?.stepId || '')))
    .map((it) => String(it?.scoredOn || '').slice(0, 10))
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    .sort();
  if (dates.length === 0) return 1;
  const first = dates[0];
  if (todayIso <= first) return 1;
  const diffDays = Math.floor((dayToUtc(todayIso) - dayToUtc(first)) / 86_400_000);
  return Math.min(PROGRAM_DAYS, Math.max(1, diffDays + 1));
};

const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const plural = (n, one, few, many) => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
};

const TASK_LABEL = { objects: 'Предметы', letters: 'Буквы' };

const hintFromError = (err) => {
  const status = err?.status ?? err?.response?.status;
  if (status === 403 || status === 404) {
    return 'Если вы врач: открывайте тренажёр из карточки пациента — /visual?patientId=UUID пациента.';
  }
  return null;
};

export default function VisualPerceptionPage() {
  // phases: loading | running | saving | done | error (+ intro — фолбэк)
  const [phase, setPhase] = useState('loading');
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [day, setDay] = useState(null);
  const [liveText, setLiveText] = useState('');
  const [listening, setListening] = useState(false); // микрофон уже включён
  const [imageState, setImageState] = useState('loading'); // loading | ok | error
  const [imageIdx, setImageIdx] = useState(0);         // какой кандидат-URL пробуем

  // --- Web Speech API -------------------------------------------------------
  const recognitionRef = useRef(null);
  const runningRef = useRef(false);
  const finalsRef = useRef([]); // финальные сегменты распознавания
  const interimRef = useRef('');
  const startedAtRef = useRef(0);
  const firstSpeechAtRef = useRef(0); // момент первого распознанного слова («раздумье»)
  const finishedRef = useRef(false); // финиш уже запущен (авто/кнопка/лимит)
  const autoStartedRef = useRef(false);
  const replayingRef = useRef(false);   // идёт повторное проговаривание вопроса

  // --- звук вопроса -----------------------------------------------------------
  const speakTimerRef = useRef(0);
  const audioRef = useRef(null);        // Audio с голосовой инструкцией (audio_url)
  const audioTimerRef = useRef(0);
  const softLimitRef = useRef(0);

  const supported = useMemo(() => isRecognitionSupported(), []);

  const answers = useMemo(() => (Array.isArray(session?.answers) ? session.answers : []), [session]);
  const task = session?.task === 'letters' ? 'letters' : 'objects';
  const softLimitMs = useMemo(() => {
    const s = Number(session?.config?.softLimitSec);
    return s > 0 ? s * 1000 : DEFAULT_SOFT_LIMIT_MS;
  }, [session]);

  // Кандидаты URL картинки: как в visual_bank + те же имена с .png/.jpg/.jpeg.
  // Методисты заливают PNG — а в банке может остаться .jpg: перебираем сами,
  // без правок базы. Относительный путь считаем от корня сайта.
  const imageCandidates = useMemo(() => {
    const raw = String(session?.imageUrl || '').trim();
    if (!raw) return [];
    const base = /^(https?:|data:|blob:)/i.test(raw) || raw.startsWith('/')
      ? raw
      : `/${raw}`;
    const m = base.match(/\.([a-z0-9]+)(\?.*)?$/i);
    const withExt = (e) =>
      m ? base.replace(/\.([a-z0-9]+)(\?.*)?$/i, `.${e}${m[2] || ''}`) : `${base}.${e}`;
    return [...new Set([base, withExt('png'), withExt('jpg'), withExt('jpeg')])];
  }, [session]);

  // Разбор финального transcript + отправка на сервер (счёт считает сервер).
  const finishRun = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    runningRef.current = false;
    window.clearTimeout(softLimitRef.current);
    setListening(false);
    try {
      recognitionRef.current?.stop();
    } catch {
      /* уже остановлен — не страшно */
    }
    recognitionRef.current = null;

    const raw = [...finalsRef.current, interimRef.current].filter(Boolean).join(' ');
    const transcript = raw.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean).slice(0, MAX_WORDS).join(' ');
    const elapsedMs = startedAtRef.current ? Date.now() - startedAtRef.current : null;
    // «На раздумье»: от включения микрофона до первого слова. Если пациент
    // так и не сказал ничего — всё задание ушло на раздумье (= elapsedMs).
    const thinkingMs = startedAtRef.current
      ? (firstSpeechAtRef.current
          ? Math.max(0, firstSpeechAtRef.current - startedAtRef.current)
          : elapsedMs)
      : null;

    setPhase('saving');
    try {
      // stepId — ПО КАРТОЧКЕ ('v01'..'v20'), оба занятия пары пишут в один шаг.
      const res = await saveVisualReport({
        patientId,
        stepId: stepIdForDay(day),
        transcript,
        elapsedMs,
        thinkingMs,
        today: localToday(),
      });
      setResult(res);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось сохранить результат.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [patientId, day]);

  const finishRef = useRef(finishRun);
  useEffect(() => {
    finishRef.current = finishRun;
  }, [finishRun]);

  // АВТОФИНИШ и живой прогресс: пересчитываем матчинг по всем финальным
  // сегментам. Всё названо → закрываем задание сами.
  useEffect(() => {
    if (phase !== 'running' || finishedRef.current || answers.length === 0) return;
    const { matched } = matchTranscript(finalsRef.current.join(' '), answers, task);
    if (matched.size >= answers.length) finishRef.current();
  }, [phase, liveText, answers, task]);

  // Предупреждение при случайном закрытии вкладки посреди задания.
  useEffect(() => {
    if (phase !== 'running') return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  // Остановить распознавание и звук при размонтировании страницы.
  useEffect(
    () => () => {
      runningRef.current = false;
      window.clearTimeout(softLimitRef.current);
      window.clearTimeout(speakTimerRef.current);
      window.clearTimeout(audioTimerRef.current);
      try {
        audioRef.current?.pause();
      } catch {
        /* ignore */
      }
      audioRef.current = null;
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* ignore */
      }
    },
    [],
  );

  const load = useCallback(async () => {
    setPhase('loading');
    setErrorMsg('');
    setErrorHint('');
    setSession(null);
    setResult(null);
    setLiveText('');
    setListening(false);
    setImageState('loading');
    setImageIdx(0);
    try {
      const params = new URLSearchParams(window.location.search);
      const pidOverride = params.get('patientId');
      const dayOverride = Number(params.get('day')) || null;

      const uid = pidOverride || (await getAuthUserId());
      if (!uid) {
        setErrorMsg('Не удалось определить пользователя. Войдите в аккаунт и откройте тренажёр заново.');
        setPhase('error');
        return;
      }

      const list = await listTrainerReports({ limit: 200 });
      const d = dayOverride
        || resolveDayFromReports(
          pidOverride ? (Array.isArray(list) ? list : list?.items || []) : (Array.isArray(list) ? list : list?.items || []),
          localToday(),
        );
      // Занятие d → карточка ceil(d/2) (2 занятия подряд на карточку).
      const data = await getVisualSession(cardForDay(d));

      setPatientId(uid);
      setDay(d);
      setSession(data);
      // Старт СРАЗУ, без экрана «Коснитесь экрана» (как в «Беглости»).
      autoStartedRef.current = false;
      replayingRef.current = false;
      finishedRef.current = false;
      // СРАЗУ показываем карточку (без вводных экранов); автостарт микрофона
      // и голос вопроса — эффектом ниже.
      setPhase(supported ? 'running' : 'intro');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось загрузить задание.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [supported]);

  useEffect(() => {
    void load();
  }, [load]);

  // СТАРТ ЗАДАНИЯ: микрофон → распознавание → фаза running (картинка по центру).
  // Вызывается ПОСЛЕ конца голосовой инструкции (или после отсчёта, если аудио нет).
  const startRun = useCallback(async () => {
    // Микрофон уже разрешён (beginSession) — перепроверим (не съедая времени):
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch {
      runningRef.current = false;
      setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону в настройках браузера и попробуйте ещё раз.');
      setPhase('error');
      return;
    }

    const rec = createRecognition({ lang: 'ru-RU', continuous: true, interimResults: true });
    if (!rec) {
      setErrorMsg('Браузер не поддерживает распознавание речи. Откройте тренажёр в Google Chrome или Яндекс.Браузере.');
      setPhase('error');
      return;
    }

    finalsRef.current = [];
    interimRef.current = '';
    firstSpeechAtRef.current = 0; // «раздумье» считается заново
    setLiveText('');
    startedAtRef.current = Date.now();
    finishedRef.current = false;
    runningRef.current = true;
    recognitionRef.current = rec;

    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const text = String(res?.[0]?.transcript || '').trim();
        if (!text) continue;
        if (!firstSpeechAtRef.current) firstSpeechAtRef.current = Date.now();
        if (res.isFinal) finalsRef.current.push(text);
        else interim += `${text} `;
      }
      interimRef.current = interim.trim();
      setLiveText([...finalsRef.current, interimRef.current].filter(Boolean).join(' '));
    };

    rec.onerror = (event) => {
      const code = event?.error || '';
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        runningRef.current = false;
        finishedRef.current = true;
        window.clearTimeout(softLimitRef.current);
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
        setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону (значок в адресной строке) и попробуйте ещё раз.');
        setPhase('error');
      }
      // no-speech / aborted / network — переживаем: onend перезапустит распознавание.
    };

    rec.onend = () => {
      if (!runningRef.current || replayingRef.current) return;
      window.setTimeout(() => {
        if (!runningRef.current) return;
        try {
          rec.start();
        } catch {
          /* уже запущен — не страшно */
        }
      }, 300);
    };

    try {
      rec.start();
    } catch {
      /* уже запущен — не страшно */
    }
    setListening(true);
    setPhase('running');

    // Мягкий лимит: страховка, если пациент не назвал всё и не нажал «Готово».
    window.clearTimeout(softLimitRef.current);
    softLimitRef.current = window.setTimeout(() => finishRef.current(), softLimitMs);
  }, [softLimitMs]);

  const startRunRef = useRef(startRun);
  useEffect(() => {
    startRunRef.current = startRun;
  }, [startRun]);

  // ГОЛОС ВОПРОСА: проговариваем questionText (speechSynthesis, ru-RU).
  // Если браузер заглушил автоголос (нет пользовательской активации) — не ждём:
  // через VOICE_GRACE_MS стартуем задание, вопрос остаётся ТЕКСТОМ на экране,
  // а кнопка 🔊 проговорит его по касанию (повтор — с паузой микрофона).
  const speakQuestion = useCallback((text, onDone) => {
    const synth = window.speechSynthesis;
    let done = false;
    let started = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(speakTimerRef.current);
      try {
        synth?.cancel();
      } catch {
        /* ignore */
      }
      onDone?.();
    };
    const question = String(text || '').trim();
    if (!synth || !question) {
      finish();
      return;
    }
    try {
      const u = new SpeechSynthesisUtterance(question);
      u.lang = 'ru-RU';
      u.rate = 0.95;
      u.onstart = () => {
        started = true;
        window.clearTimeout(speakTimerRef.current);
        // Голос пошёл — страховка на случай, если onend не придёт никогда.
        speakTimerRef.current = window.setTimeout(finish, VOICE_CAP_MS);
      };
      u.onend = finish;
      u.onerror = finish;
      synth.cancel();
      synth.speak(u);
    } catch {
      finish();
      return;
    }
    // Автоголос заглушён (onstart не случится) — стартуем без голоса.
    window.clearTimeout(speakTimerRef.current);
    speakTimerRef.current = window.setTimeout(() => {
      if (!started) finish();
    }, VOICE_GRACE_MS);
  }, []);

  // Остановить аудиофайл инструкции (при повторе/размонтировании/таймауте).
  const stopAudio = useCallback(() => {
    window.clearTimeout(audioTimerRef.current);
    try {
      audioRef.current?.pause();
    } catch {
      /* ignore */
    }
    audioRef.current = null;
  }, []);

  // ОЗВУЧКА ВОПРОСА: СНАЧАЛА СВОЙ АУДИОФАЙЛ из visual_bank (audio_url) —
  // как было в исходной версии с экраном инструкции. Файл сыграл — микрофон;
  // файла нет / не загрузился / браузер заглушил автоплей — фолбэк на
  // синтез речи (speakQuestion), у него свои страховки на заглушённый голос.
  const playInstruction = useCallback((onDone) => {
    const audioUrl = typeof session?.audioUrl === 'string' ? session.audioUrl.trim() : '';
    if (audioUrl) {
      let done = false;
      const finish = (played) => {
        if (done) return;
        done = true;
        stopAudio();
        if (played) {
          onDone?.();
        } else {
          speakQuestion(session.questionText, onDone);
        }
      };
      try {
        const a = new Audio(audioUrl);
        a.preload = 'auto';
        audioRef.current = a;
        a.onended = () => finish(true);
        a.onerror = () => finish(false);
        // страховка от «зависшего» файла: через AUDIO_CAP_MS стопаем и стартуем
        audioTimerRef.current = window.setTimeout(() => finish(true), AUDIO_CAP_MS);
        const p = a.play();
        if (p && typeof p.catch === 'function') p.catch(() => finish(false));
        return;
      } catch {
        finish(false);
        return;
      }
    }
    speakQuestion(session.questionText, onDone);
  }, [session, speakQuestion, stopAudio]);

  // СТАРТ: предзапрос микрофона (диалог разрешения не должен звучать поверх
  // вопроса) → озвучить вопрос (свой аудиофайл, иначе TTS) → распознавание.
  // auto=true — автозапуск после загрузки: если микрофон не дали — тихо
  //   уходим в фолбэк «Коснитесь экрана», без ошибок на экране.
  // auto=false — запуск по касанию (фолбэк): ошибка микрофона — честная ошибка.
  const beginSession = useCallback(async (auto = false) => {
    if (!supported || !session) return;
    setErrorMsg('');
    setErrorHint('');

    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch {
      if (auto) {
        setPhase('intro');
        return;
      }
      setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону в настройках браузера и попробуйте ещё раз.');
      setPhase('error');
      return;
    }

    playInstruction(() => startRunRef.current());
  }, [session, playInstruction, supported]);

  // АВТОСТАРТ: карточка показана → сразу микрофон + голос вопроса.
  // Повторный запуск возможен только по касанию (beginSession(false)).
  useEffect(() => {
    if (phase !== 'running' || !session || autoStartedRef.current) return;
    autoStartedRef.current = true;
    void beginSession(true);
  }, [phase, session, beginSession]);

  // Повтор вопроса по 🔊: на время звука ставим микрофон на паузу,
  // иначе распознавание «услышит» сам вопрос и запишет его в ошибки.
  // Играет ТОТ ЖЕ источник, что и на старте: свой аудиофайл, иначе TTS.
  const replayQuestion = useCallback(() => {
    if (!session || replayingRef.current) return;
    const wasRunning = runningRef.current;
    replayingRef.current = true;
    if (wasRunning) {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
    }
    playInstruction(() => {
      replayingRef.current = false;
      if (wasRunning && recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch {
          /* уже запущен — не страшно */
        }
      }
    });
  }, [session, playInstruction]);

  // Живой прогресс по локальному матчингу (счёт — серверный).
  const localMatch = useMemo(() => {
    if (phase !== 'running' || answers.length === 0) return { matched: new Set(), errors: [] };
    return matchTranscript(finalsRef.current.join(' '), answers, task);
  }, [phase, liveText, answers, task]);
  const matchedCount = localMatch.matched.size;

  const taskLabel = TASK_LABEL[task];
  const dayNum = Number(day) || 0;
  const count = Number(result?.count ?? 0);
  const total = Number(result?.total ?? answers.length);
  const matchedList = Array.isArray(result?.matched) ? result.matched : [];
  const missedList = Array.isArray(result?.missed) ? result.missed : [];
  const errorList = Array.isArray(result?.errors) ? result.errors : [];
  const elapsedMs = result?.elapsedMs;
  const thinkingMs = result?.thinkingMs;

  return (
    <div className="flex min-h-screen flex-col bg-cvx-background px-4 py-6">
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col">
        {/* Шапка: логотип 1:1 с лендингом + «Назад» */}
        <header className="flex items-center justify-between gap-3">
          <a href="/" aria-label="Cortivox.ai на главную" className="flex items-center gap-2.5">
            <span className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white">
              <BrainCircuit className="size-4" />
            </span>
            <span className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">
              Cortivox<span className="text-cvx-logo-ai">.ai</span>
            </span>
          </a>
          <button type="button" onClick={() => window.history.back()} className="cvx-back">
            <ArrowLeft className="size-4" /> Назад
          </button>
        </header>

        {/* ЗАГРУЗКА */}
        {phase === 'loading' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-cvx-muted-foreground">
            <Loader2 className="size-8 animate-spin text-cvx-primary" />
            <p className="text-sm font-semibold">Загружаем задание…</p>
          </div>
        )}

        {/* СОХРАНЕНИЕ */}
        {phase === 'saving' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-cvx-muted-foreground">
            <Loader2 className="size-8 animate-spin text-cvx-primary" />
            <p className="text-sm font-semibold">Сохраняем результат…</p>
          </div>
        )}

        {/* ФОЛБЭК: только при прямом открытии страницы (нет активации) */}
        {phase === 'intro' && session && (
          <section
            role="button"
            tabIndex={0}
            aria-label="Коснитесь экрана, чтобы начать"
            onClick={() => void beginSession(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void beginSession(false);
              }
            }}
            className={`mt-6 flex flex-1 flex-col ${supported ? 'cursor-pointer select-none' : 'cursor-default'}`}
          >
            <div className="flex flex-1 flex-col items-center justify-center rounded-3xl border border-cvx-border bg-cvx-card p-8 text-center shadow-sm">
              {taskLabel && (
                <span className="inline-flex items-center rounded-full bg-cvx-accent px-4 py-1.5 text-sm font-bold text-cvx-accent-foreground">
                  День {dayNum} · {taskLabel}
                </span>
              )}

              <span className="mt-10 flex size-20 items-center justify-center rounded-full bg-cvx-primary/10">
                <Eye className="size-9 text-cvx-primary" />
              </span>

              <p className="mt-8 text-2xl font-bold tracking-tight text-cvx-foreground">
                Коснитесь экрана
              </p>
              <p className="mt-2 text-sm text-cvx-muted-foreground">
                {session.questionText || 'Задание начнётся сразу после касания'}
              </p>

              {!supported && (
                <div className="mt-8 flex items-start gap-2.5 rounded-2xl border border-cvx-warn/30 bg-cvx-warn-card p-4 text-left">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-cvx-warn" />
                  <p className="text-sm font-semibold text-cvx-warn">
                    Браузер не поддерживает распознавание речи. Откройте тренажёр в Google Chrome или Яндекс.Браузере.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ЗАДАНИЕ: вопрос + картинка по центру, пациент называет вслух */}
        {phase === 'running' && session && (
          <section className="flex flex-1 flex-col items-center pt-6">
            {taskLabel && (
              <span className="inline-flex items-center rounded-full bg-cvx-accent px-4 py-1.5 text-sm font-bold text-cvx-accent-foreground">
                День {dayNum} · {taskLabel}
              </span>
            )}

            {/* ВОПРОС: текстом на экране + 🔊 проговорить ещё раз */}
            <div className="mt-3 flex w-full items-center justify-center gap-2">
              <p className="text-lg font-bold tracking-tight text-cvx-foreground">
                {session.questionText}
              </p>
              <button
                type="button"
                onClick={replayQuestion}
                aria-label="Проговорить вопрос ещё раз"
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-cvx-secondary/40 text-cvx-primary transition-colors hover:bg-cvx-secondary"
              >
                <Volume2 className="size-4" />
              </button>
            </div>

            {/* КАРТИНКА ПО ЦЕНТРУ ЭКРАНА */}
            <div className="mt-4 flex w-full flex-1 flex-col items-center justify-center gap-3">
              <img
                key={imageIdx}
                src={imageCandidates[imageIdx] || session.imageUrl}
                alt="Наложенные фигуры: назовите, что видите"
                onLoad={() => setImageState('ok')}
                onError={() => {
                  if (imageIdx < imageCandidates.length - 1) setImageIdx((i) => i + 1);
                  else setImageState('error');
                }}
                className="max-h-[52vh] w-auto max-w-full rounded-2xl border border-cvx-border bg-cvx-card object-contain shadow-sm"
                draggable={false}
              />
              {imageState === 'error' && (
                <p className="flex items-center gap-2 rounded-2xl border border-cvx-warn/30 bg-cvx-warn-card px-4 py-2.5 text-xs font-semibold text-cvx-warn">
                  <AlertTriangle className="size-3.5 shrink-0" />
                  Картинка не загрузилась ({imageCandidates[0] || 'нет пути в visual_bank'}):
                  нужен файл cardNN.png или .jpg по этому пути.
                </p>
              )}
            </div>

            {/* Прогресс: названо X из N + АНОНИМНЫЕ точки (список ответов
                НЕ показываем — пациент не видит подсказок). Локальный матчинг
                только для подсветки/автофиниша, авторитетный счёт — сервер. */}
            <div className="mt-4 flex flex-col items-center gap-2" aria-live="polite">
              <span className="rounded-full bg-cvx-secondary px-3 py-1 text-xs font-bold text-cvx-secondary-foreground">
                Названо {matchedCount} из {answers.length}
              </span>
              <div className="flex max-w-xs flex-wrap items-center justify-center gap-1.5">
                {answers.map((a, i) => (
                  <span
                    key={`dot-${i}`}
                    className={`size-2.5 rounded-full transition-colors ${
                      localMatch.matched.has(i)
                        ? 'bg-[oklch(0.7_0.15_150)]'
                        : 'bg-cvx-muted-foreground/25'
                    }`}
                  />
                ))}
              </div>
            </div>

            <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-cvx-foreground">
              {listening ? (
                <>
                  <Mic className="size-4 animate-pulse text-cvx-primary" />
                  Говорите
                </>
              ) : (
                <>
                  <Volume2 className="size-4 animate-pulse text-cvx-primary" />
                  Слушайте вопрос…
                </>
              )}
            </p>

            <div className="mt-2 min-h-20 w-full rounded-2xl border border-cvx-border bg-cvx-muted p-4" aria-live="polite">
              <p className={`break-words text-base leading-snug ${liveText ? 'text-cvx-foreground' : 'text-cvx-muted-foreground/60'}`}>
                {liveText || '…'}
              </p>
            </div>

            <button
              type="button"
              onClick={() => finishRef.current()}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-cvx-border bg-cvx-card px-6 py-3 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
            >
              <Flag className="size-4" /> Готово
            </button>
          </section>
        )}

        {/* РЕЗУЛЬТАТ */}
        {phase === 'done' && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="rounded-3xl border border-cvx-border bg-cvx-card p-6 text-center shadow-sm sm:p-8">
              <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-cvx-accent">
                <CheckCircle2 className="size-7 text-cvx-accent-foreground" />
              </span>
              <h1 className="mt-4 text-xl font-bold text-cvx-foreground">Задание выполнено — результат сохранён</h1>
              <p className="mt-1 text-sm font-semibold text-cvx-muted-foreground">
                День {dayNum} · {taskLabel}
              </p>

              <p className="mt-6 text-6xl font-bold tabular-nums text-cvx-primary">{count}</p>
              <p className="mt-1 text-sm font-semibold text-cvx-muted-foreground">
                {plural(count, 'назван верно', 'названо верно', 'названо верно')} из {total}
              </p>
              {(elapsedMs != null || thinkingMs != null) && (
                <p className="mt-1 text-sm font-semibold text-cvx-muted-foreground">
                  {elapsedMs != null ? `Общее время ${fmtElapsed(elapsedMs)}` : 'Время не зафиксировано'}
                  {thinkingMs != null ? ` · на раздумье ${fmtElapsed(thinkingMs)}` : ''}
                </p>
              )}

              {matchedList.length > 0 && (
                <div className="mt-5 rounded-2xl bg-cvx-muted p-4 text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cvx-muted-foreground/60">
                    Названо верно
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-cvx-foreground">{matchedList.join(', ')}</p>
                </div>
              )}
              {missedList.length > 0 && (
                <div className="mt-3 rounded-2xl border border-cvx-warn/30 bg-cvx-warn-card p-4 text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cvx-muted-foreground/60">
                    Не названо
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-cvx-foreground">{missedList.join(', ')}</p>
                </div>
              )}
              {errorList.length > 0 && (
                <div className="mt-3 rounded-2xl bg-cvx-muted p-4 text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cvx-muted-foreground/60">
                    Лишние слова (ошибок: {errorList.length})
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-cvx-foreground">{errorList.join(', ')}</p>
                </div>
              )}

              <button
                type="button"
                onClick={() => window.history.back()}
                className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-cvx-border bg-cvx-card px-8 py-3.5 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
              >
                Готово
              </button>
            </div>
          </section>
        )}

        {/* ОШИБКА */}
        {phase === 'error' && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="rounded-3xl border border-cvx-border bg-cvx-card p-6 text-center shadow-sm sm:p-8">
              <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-cvx-warn">
                <AlertTriangle className="size-7 text-cvx-warn" />
              </span>
              <h1 className="mt-4 text-xl font-bold text-cvx-foreground">Что-то пошло не так</h1>
              <p className="mt-2 text-sm leading-relaxed text-cvx-muted-foreground">{errorMsg}</p>
              {errorHint && <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">{errorHint}</p>}
              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  onClick={() => void load()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cvx-primary px-6 py-3 text-sm font-bold text-cvx-primary-foreground transition-colors hover:bg-cvx-primary/90"
                >
                  <RotateCcw className="size-4" /> Повторить
                </button>
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="inline-flex items-center justify-center rounded-full border border-cvx-border bg-cvx-card px-6 py-3 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
                >
                  Назад
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
